<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { avatarHue } from '../utils/avatar-hue';

const props = withDefaults(
  defineProps<{
    name?: string;
    /** sm 24 · md 28 · lg 44 · xl 64 (the profile photo editor and the person header). */
    size?: 'sm' | 'md' | 'lg' | 'xl';
    color?: string;
    src?: string;
    /** The signed-in person: espresso with cream initials instead of a categorical
     * hue (spec §6), so "you" reads the same in every list and in the account row. */
    self?: boolean;
  }>(),
  { name: '', size: 'md', self: false },
);

// The hue lives in `utils/avatar-hue` so a caller that seeds it by something other
// than the name (people-ui seeds by person id, so a rename recolours nobody) uses
// the SAME mapping and passes `color` (#413) — a second copy of this hash and
// palette is how one person ends up two colours in two parts of the suite.
const background = computed(() => (props.self ? undefined : props.color || avatarHue(props.name)));

// A photo that fails to load falls back to the initials chip rather than a broken
// image; a new source gets a fresh try (e.g. after an upload).
const failed = ref(false);
watch(
  () => props.src,
  () => {
    failed.value = false;
  },
);
const showPhoto = computed(() => !!props.src && !failed.value);

// A person's name ("Anna-Lena Müller", "Ada Lovelace") gives the first letter of
// the first word and of the last — the same two letters People's own avatar drew
// from firstName/lastName, so a hyphenated first name is "AM", not "AL". A single
// token is an identifier ("ada.lovelace@acme.de", "ops_team"), and that one still
// splits at . @ _ - to find its parts.
function initialsOf(raw: string): string {
  const words = raw.split(/\s+/).filter(Boolean);
  const parts = words.length > 1 ? [words[0], words[words.length - 1]] : (words[0] ?? '').split(/[.@_-]+/).filter(Boolean);
  return parts
    .map((p) => p[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}
const initials = computed(() => initialsOf(String(props.name || '?')) || '?');
</script>

<template>
  <span
    class="lumi-avatar"
    :class="[`lumi-avatar--${size}`, { 'lumi-avatar--self': self }]"
    :style="{ background: showPhoto ? undefined : background }"
    :title="name"
  >
    <img v-if="showPhoto" class="lumi-avatar__img" :src="src" :alt="name" @error="failed = true" />
    <template v-else>{{ initials }}</template>
  </span>
</template>

<style>
/* Categorical ground, espresso initials (spec §6): the hue tells people apart,
   the initials stay legible on every hue because they never change colour. */
.lumi-avatar{display:inline-flex;align-items:center;justify-content:center;border-radius:50%;overflow:hidden;background:var(--avatar-hue-1);color:var(--avatar-fg);font-family:var(--font-body);font-weight:var(--weight-semibold);line-height:1;text-align:center;flex-shrink:0;user-select:none;}
.lumi-avatar--self{background:var(--avatar-self-bg);color:var(--avatar-self-fg);}
.lumi-avatar__img{width:100%;height:100%;object-fit:cover;border-radius:50%;display:block;}
.lumi-avatar--sm{width:24px;height:24px;font-size:10px;}
.lumi-avatar--md{width:28px;height:28px;font-size:11px;}
.lumi-avatar--lg{width:44px;height:44px;font-size:16px;}
.lumi-avatar--xl{width:64px;height:64px;font-size:24px;}
</style>
